import React, { useState } from 'react';
import { getOccasionText } from '../../../utils/getOcassionIcon';
import { ContanierSection, Divider } from '../../../uiComponents/common';
import { useTranslation } from 'react-i18next';
import { Show } from '../../../components';
import { getLocale2 } from '../../../helpers/dates';
import { hasAllowedRoles } from '../../../helpers/roles';
import { useUser } from '../../../hooks';
import { Link } from 'react-router-dom';
import '../reservation-detail.css';
import '../../fechasEspeciales/fechasEspeciales.css';
import { useSelector } from 'react-redux';
import { format } from 'date-fns';
import { EditScheduleModal } from './editScheduleModal';
import { Button } from '@/components/ui/button';
import { Edit, CalendarDays } from 'lucide-react';

const ReservationInfo = ({ reservation, mode = 'detail', canChangeTime = false, callback }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'components.reservationInfo' });
  const { t: translate } = useTranslation();
  const language = useSelector((state) => state.app.language);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { roles } = useUser();

  const specialDateId = reservation?.specialDateId;
  const specialDateName = reservation?.specialDateName;
  const canViewSpecialDates = hasAllowedRoles(roles, [
    'superadministrator',
    'marketadmin',
    'admin',
    'productloader',
    'productpriceloader',
  ]);

  const getReservation = () => {
    let response = {};
    if (mode === 'detail') {
      response.venue = reservation.activityName;
      response.date = reservation.reservationDateLocalTime;
      response.customer = reservation.customerName;
      response.pax = reservation.paxs;
      response.email = reservation.emailClient;
      response.id = reservation?.id;
    } else {
      response.venue = reservation?.actualReservation?.activityName;
      response.date = reservation?.actualReservation?.date;
      response.customer = reservation.customer;
      response.pax = reservation?.actualReservation?.pax;
      response.email = reservation?.email;
      response.id = reservation?.actualReservation?.id;
    }
    return response;
  };

  const locale = getLocale2(language);

  const date = format(new Date(getReservation()?.date), 'EEEE d MMM.', { locale });
  const time = format(new Date(getReservation()?.date), 'HH:mm a.', { locale });
  return (
    <>
      <ContanierSection maxWidth='1200px'>
        <div className='row no-gutters container-data margin-reservation'>
          <div className='col-12 col-md-3'>
            <div className='dark-grey container-label'>Venue</div>
            <div className='fw-700 container-text'>{getReservation().venue}</div>
          </div>
          <div className='col-12 col-md-4'>
            <div className='dark-grey container-label'>{t('details.dateReservation')}</div>
            <div className='fw-700 container-text sentences-capitalize'>{date}</div>
          </div>
          <div className='col-12 col-md-4 d-flex tw-items-end tw-gap-4'>
            <div>
              <div className='dark-grey container-label'>{t('details.time')}</div>
              <div className='fw-700 container-text text-lowercase'>{time}</div>
            </div>
            {canChangeTime ? (
              <Button
                variant='outline'
                size='sm'
                onClick={() => setIsEditModalOpen(true)}
                className='tw-flex tw-items-center tw-space-x-2'
              >
                <Edit className='tw-h-4 tw-w-4' />
                <span>{t('editTime')}</span>
              </Button>
            ) : null}
          </div>
        </div>

        <div className={`row no-gutters container-data  margin-reservation`}>
          <div className='col-12 col-md-3'>
            <div className='dark-grey container-label'>{t('details.nameOf')}</div>
            <div className='fw-700 container-text'>
              {getReservation().customer ? getReservation().customer : '-'}
            </div>
          </div>
          <div className='col-12 col-md-4 text-truncate'>
            <div className='dark-grey container-label'>{t('details.email')}</div>
            <div className='fs-16 fw-400'>
              {getReservation().email ? getReservation().email : '-'}
            </div>
          </div>
          <div className='col-12 col-md-4'>
            <div className='dark-grey  container-label'>{t('details.clientPhone')}</div>
            <div className='fs-16 fw-400'>{reservation.customerPhone}</div>
          </div>
        </div>
        <div className={`row no-gutters container-data`}>
          <div className='col-12 col-md-3'>
            <div className='dark-grey container-label'>{t('details.pax')}</div>
            <div className='fw-700 container-text'>
              {reservation.reservationType === 'RESTAURANT'
                ? getReservation().pax
                : reservation?.reservationDetail?.adultPaxs}
            </div>
          </div>
          <Show.When isTrue={!!reservation.attribute}>
            <div className='col-12 col-md-3'>
              <div className='dark-grey container-label'>{t('details.attribute')}</div>
              <div className='fs-16 fw-400'>
                {t(`details.attributeValue.${reservation.attribute}`)}
              </div>
            </div>
          </Show.When>
        </div>
      </ContanierSection>
      <Divider className='mb-26' />
      <ContanierSection maxWidth='1200px'>
        <div className='row no-gutters container-data margin-reservation'>
          <div className='col-12 col-md-3 '>
            <div className='dark-grey  container-label'>{t('details.chance')}</div>
            <div className='container-text fw-600'>
              {getOccasionText(reservation.occasion, translate) ?? '-'}
            </div>
          </div>
          <div className='col-12 col-md-4 '>
            <div className='dark-grey  container-label'>{t('details.event')}</div>
            <div className='container-text fw-600'>
              {reservation.eventName ? reservation.eventName : '-'}
            </div>
          </div>
          <Show.When isTrue={reservation.status === 'CANCELLED' && mode !== 'detail'}>
            <div className='col-12 col-md-4'>
              <div className='dark-grey container-label'>{t('details.cancelBy')}</div>
              <div className='fw-700 container-text'>
                {reservation?.hasModification
                  ? t('details.modify')
                  : reservation?.cancelledByUserId
                  ? 'Concierge'
                  : t('details.client')}
              </div>
            </div>
          </Show.When>
        </div>
        <div className='row no-gutters container-data'>
          <div className='col-12'>
            <div className='dark-grey  container-label'>{t('details.specialRequest')}</div>
            <div className='fs-16 fw-400'>{reservation.comments ? reservation.comments : '-'}</div>
          </div>
        </div>
        <EditScheduleModal
          date={getReservation()?.date}
          isOpen={isEditModalOpen}
          reservationId={getReservation()?.id}
          onClose={() => setIsEditModalOpen(false)}
          callback={callback}
        />
      </ContanierSection>
      {specialDateId && (
        <ContanierSection maxWidth='1200px'>
          <div className='fe-special-date-banner'>
            <div className='fe-special-date-banner-icon'>
              <CalendarDays size={18} />
            </div>
            <div className='fe-special-date-banner-content'>
              <div className='fe-special-date-banner-title'>
                {t('specialDateBanner.label')}:{' '}
                <span className='fe-special-date-banner-name'>{specialDateName}</span>
              </div>
              <div className='fe-special-date-banner-desc'>
                {t('specialDateBanner.description')} <strong>{specialDateName}</strong>
              </div>
            </div>
            {canViewSpecialDates && (
              <Link to={`/special-dates/${specialDateId}`} className='fe-special-date-banner-link'>
                {t('specialDateBanner.viewLink')}
              </Link>
            )}
          </div>
        </ContanierSection>
      )}
    </>
  );
};

export default ReservationInfo;
